// Ruch części 1 hero 47-A „parabola fanbase” (paczka H47, MAPA §2): czas → stan, strona przewija się zwyczajnie.
//
// Właściciel 27.09: „scroll nie ma lockować scrollowania STRONY”. Dlatego tutaj NIE MA przypięcia, nasłuchu
// `wheel` / `touchmove`, `preventDefault` ani sterowania ruchem przez przewinięcie. Przewijanie rusza wyłącznie
// lekką paralaksą pola grafiki (0,12 × scrollY do 1400 px, nasłuch pasywny + `requestAnimationFrame`, jeden
// `translate3d`). Cała reszta to funkcja czasu od wejścia w widok: zdarzenia z `lib/landingParabola.ts`
// (`planFala`, `planBudowa`) odpalane przez Web Animations API (tylko `transform` i `opacity`, kompozytor).
//
// PIERWSZE MALOWANIE = KLATKA SPOCZYNKU (MAPA §8, dogrywka HD2): statyczny HTML i pierwszy render Reacta
// rysują świat w spoczynku. Montaż z polem grafiki w widoku gra SALWĘ, która animuje tylko ku stanowi
// spoczynku (nic nie znika, więc nic nie mignie): 350 ms po `load`, ale najpóźniej 900 ms po montażu (MAPA §2;
// przegląd Claude H47-CLAUDE-04: jedno wiszące żądanie obrazu zatrzymywało salwę i pętlę na zawsze). Stan
// wyjściowy BUDOWY (paczki i konta w skali 0) jest ustawiany WYŁĄCZNIE wtedy, gdy namalowanego pola nie widać,
// a budowa gra przy powrocie w widok. Poza ekranem: animacje anulowane, zegar pętli zatrzymany. Pętla co 5600 ms
// dowozi nową paczkę na koniec ramion.
//
// „W WIDOKU” = pod przyklejonym nagłówkiem (przegląd Claude H47-CLAUDE-01): obserwator patrzy na wycinek
// `.mp-hp-gw` (bez transformu paralaksy) z `rootMargin` minus wysokość nieprzezroczystego nagłówka, bo inaczej
// pole schowane pod nagłówkiem dalej „przecinało okno”: pętla grała w czasie czytania części 2, a budowa przy
// powrocie nie grała wcale. Pole z paralaksą zjeżdża wolniej niż wycinek, więc jego dolny skrawek bywa jeszcze
// widoczny, gdy wycinek już zniknął: stan wyjściowy budowy czeka wtedy, aż namalowane pole naprawdę zniknie.
//
// `prefers-reduced-motion: reduce` (albo brak `matchMedia`, IntersectionObserver lub `Element.animate`):
// klatka spoczynku, zero animacji, zero paralaksy. Moduł nie dotyka `window` przy imporcie.

import { useEffect, useState } from 'react'
import type { RefObject } from 'react'
import { BUDOWA, HERO_H_PX, HERO_W_PX, OKRES, planBudowa, planFala } from '../lib/landingParabola'
import { PUBLIC_HEADER_H } from '../components/public/PublicHeader'
import type { Orient, ParabolaData, Zdarzenie } from '../lib/landingParabola'

export const HERO_H_QUERY = `(min-width: ${HERO_H_PX}px)`
export const HERO_W_QUERY = `(min-width: ${HERO_W_PX}px)`
export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'
/** Paralaksa: grafika jedzie wolniej niż strona (MAPA §2), tylko w pierwszych 1400 px przewinięcia. */
export const PARALLAX = 0.12
const PARALLAX_UNTIL = 1400
/** Przyklejony nagłówek publiczny: 64 px, do 720 px szerokości 56 px (`.mp-nav.is-stuck`, minimal-public.css). */
export const STUCK_HEADER_PHONE_H = 56
export const stuckHeaderH = () =>
  (typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 720px)').matches ? STUCK_HEADER_PHONE_H : PUBLIC_HEADER_H)
/** Salwa: 350 ms po `load`, najpóźniej 900 ms po montażu (MAPA §2). */
export const SALWA_PO_LOAD = 350
export const SALWA_NAJPOZNIEJ = 900

type MediaState = { motion: boolean; orient: Orient }

function readMedia(): MediaState {
  const mm = typeof window !== 'undefined' ? window.matchMedia : undefined
  if (typeof mm !== 'function') return { motion: false, orient: 'v' }
  const q = (s: string) => mm.call(window, s).matches
  return { motion: !q(REDUCED_MOTION_QUERY), orient: q(HERO_H_QUERY) ? 'h' : q(HERO_W_QUERY) ? 'w' : 'v' }
}

/**
 * Czy ruch jest dozwolony i która orientacja świata (v < 600 px ≤ w < 1336 px ≤ h). Czytane synchronicznie
 * w inicjalizatorze stanu, żeby pierwszy render klienta narysował to samo pole co statyczny HTML.
 */
export function useHeroMedia(): MediaState {
  const [state, setState] = useState(readMedia)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const lists = [REDUCED_MOTION_QUERY, HERO_H_QUERY, HERO_W_QUERY].map((q) => window.matchMedia(q))
    const onChange = () => setState((prev) => {
      const next = readMedia()
      return next.motion === prev.motion && next.orient === prev.orient ? prev : next
    })
    for (const l of lists) l.addEventListener?.('change', onChange)
    onChange()
    return () => { for (const l of lists) l.removeEventListener?.('change', onChange) }
  }, [])
  return state
}

/** Czy przeglądarka ma wszystko, czego ruch potrzebuje (jsdom i stare silniki: klatka spoczynku). */
export function canAnimate(el: Element | null): el is HTMLElement {
  return Boolean(el) && typeof IntersectionObserver === 'function' && typeof (el as HTMLElement).animate === 'function'
}

/**
 * Zdarzenia → animacje WAAPI względem korzenia. Element z selektora szukany raz (pamięć `cache`), bo pętla
 * odpala ~100-300 zdarzeń co 5,6 s. `pauza`: animacje zatrzymane w t = 0 (stan wyjściowy sekwencji).
 */
export function odpal(root: Element, ev: Zdarzenie[], pauza: boolean, cache: Map<string, Element | null>): Animation[] {
  const out: Animation[] = []
  for (const e of ev) {
    if (!cache.has(e.sel)) cache.set(e.sel, root.querySelector(e.sel))
    const el = cache.get(e.sel)
    if (!el) continue
    const a = el.animate(e.kf, { delay: Math.max(0, e.t), duration: e.d, easing: e.e, fill: e.f ?? 'none' })
    if (pauza) { a.pause(); a.currentTime = 0 }
    out.push(a)
  }
  return out
}

/**
 * Salwa, budowa, pętla, pauza poza ekranem i paralaksa dla pola grafiki części 1.
 * `field` = pole grafiki (korzeń selektorów zdarzeń), `par` = warstwa paralaksy nad nim, `gw` = wycinek pola
 * (bez transformu, obserwowany).
 */
export function useHeroMotion(field: RefObject<HTMLElement | null>, par: RefObject<HTMLElement | null>, gw: RefObject<HTMLElement | null>, world: ParabolaData, enabled: boolean): void {
  useEffect(() => {
    const g = field.current, wrap = gw.current
    if (!enabled || !canAnimate(g) || !wrap || typeof window === 'undefined') return
    const cache = new Map<string, Element | null>()
    const mountT = performance.now()
    const headerH = stuckHeaderH()
    let anims: Animation[] = []
    let timer = 0
    let salwaTimer = 0
    let visible = false
    let armed = false // stan wyjściowy budowy ustawiony (pole poza ekranem)
    let pendingArm = false // wycinek zniknął, ale namalowane pole (paralaksa) jeszcze nie
    let first = true
    let onLoad: (() => void) | null = null

    const stop = () => {
      if (timer) window.clearTimeout(timer)
      if (salwaTimer) window.clearTimeout(salwaTimer)
      timer = 0
      salwaTimer = 0
      if (onLoad) window.removeEventListener('load', onLoad)
      onLoad = null
      for (const a of anims) a.cancel()
      anims = []
    }
    const loop = (ms: number) => {
      timer = window.setTimeout(() => {
        timer = 0
        if (!visible) return
        anims = anims.filter((a) => a.playState !== 'finished').concat(odpal(g, planFala(world, 0, false), false, cache))
        loop(OKRES)
      }, ms)
    }
    /** Namalowanego pola nie widać: całe pod nagłówkiem albo pod dolną krawędzią okna. */
    const fieldHidden = () => { const r = g.getBoundingClientRect(); return r.bottom <= headerH || r.top >= window.innerHeight }
    const arm = () => { pendingArm = false; anims = odpal(g, planBudowa(world, 0), true, cache); armed = true }
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && e.intersectionRatio >= 0.1 && !visible) {
          visible = true
          pendingArm = false
          if (armed) {
            // powrót w widok: budowa z nasady ze stanu wyjściowego ustawionego poza ekranem
            armed = false
            for (const a of anims) a.play()
            loop(BUDOWA + 300)
          } else {
            // montaż z polem w widoku (albo wejście bez uzbrojonej budowy): salwa, nic nie znika
            stop()
            let started = false
            const salwa = (t0: number) => {
              if (started || !visible) return
              started = true
              if (salwaTimer) window.clearTimeout(salwaTimer)
              salwaTimer = 0
              if (onLoad) window.removeEventListener('load', onLoad)
              onLoad = null
              anims = odpal(g, planFala(world, t0, true), false, cache)
              loop(t0 + OKRES)
            }
            if (document.readyState === 'complete') salwa(SALWA_PO_LOAD)
            else {
              // 350 ms po `load`, ale nie później niż 900 ms od montażu
              const deadline = () => Math.max(0, mountT + SALWA_NAJPOZNIEJ - performance.now())
              onLoad = () => { onLoad = null; salwa(Math.min(SALWA_PO_LOAD, deadline())) }
              window.addEventListener('load', onLoad, { once: true })
              salwaTimer = window.setTimeout(() => { salwaTimer = 0; salwa(0) }, deadline())
            }
          }
          first = false
        } else if (!e.isIntersecting && (visible || first || (!armed && !pendingArm))) {
          visible = false
          first = false
          stop()
          if (fieldHidden()) arm()
          else pendingArm = true
        }
      }
    }, { threshold: [0, 0.1], rootMargin: `-${headerH}px 0px 0px 0px` })
    io.observe(wrap)

    // lekka paralaksa: grafika jedzie wolniej niż strona (tylko transform, nasłuch pasywny); w tej samej klatce
    // odłożone uzbrojenie budowy, gdy namalowane pole wreszcie zniknie
    const p = par.current
    let raf = 0
    const onScroll = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        const y = window.scrollY
        if (p && y < PARALLAX_UNTIL) p.style.transform = `translate3d(0,${Math.round(y * PARALLAX * 10) / 10}px,0)`
        if (pendingArm && !visible && fieldHidden()) arm()
      })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => {
      io.disconnect()
      stop()
      window.removeEventListener('scroll', onScroll)
      if (raf) cancelAnimationFrame(raf)
      if (p) p.style.transform = ''
    }
  }, [field, par, gw, world, enabled])
}
